import { ChangeEvent, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  CircularProgress
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAppContext } from "../context/AppContext";
import { useFormik } from "formik";
import * as yup from "yup";

const LoginPage = () => {
  const rememberPreferenceKey = "sky-view.auth.remember";
  const initialRememberPreference = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const stored = window.localStorage.getItem(rememberPreferenceKey);
    return stored === "true";
  }, []);

  const persistRememberPreference = (value: boolean) => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(rememberPreferenceKey, value ? "true" : "false");
  };

  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession, resolveApiPath, userManagementEnabled, userManagementResolved } = useAuth();
  const { eventStreamConnected, isDemoMode } = useAppContext();
  const backendOffline = userManagementResolved && !eventStreamConnected;

  const validationSchema = yup.object({
    username: yup.string().trim().required("Username is required"),
    password: yup.string().trim().required("Password is required"),
    remember: yup.boolean()
  });

  const formik = useFormik({
    initialValues: { username: "", password: "", remember: initialRememberPreference },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting }) => {
      setError(null);
      setSubmitting(true);
      persistRememberPreference(Boolean(values.remember));
      if (backendOffline) {
        setError("Backend server is offline. Please try again when it is back online.");
        setSubmitting(false);
        return;
      }
      try {
        const response = await fetch(resolveApiPath("/api/auth/login"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: values.username, password: values.password })
        });

        const data = await response.json().catch(() => null);
        if (response.ok) {
          const tokens = data?.tokens;
          const hasAdminRights = (data?.user?.role ?? "").trim().toLowerCase() === "admin";
          setSession({
            user: data?.user ?? null,
            tokens: tokens?.accessToken && tokens?.refreshToken ? tokens : null
          }, { remember: Boolean(values.remember) });

          const requiresPasswordReset = Boolean(data?.user?.forcePasswordReset);
          let destination = requiresPasswordReset
            ? "/password-reset"
            : (location.state as { from?: string } | null)?.from ?? "/player";

          const accessToken =
            typeof tokens?.accessToken === "string" && tokens.accessToken.trim().length > 0
              ? tokens.accessToken
              : null;

          try {
            const streamsResponse = await fetch(resolveApiPath("/api/streams"), {
              headers: accessToken
                ? {
                    Authorization: `Bearer ${accessToken}`
                  }
                : undefined
            });

            const streamsPayload = (await streamsResponse.json().catch(() => null)) as
              | {
                  streams?: unknown;
                }
              | null;

            if (
              streamsResponse.ok &&
              Array.isArray(streamsPayload?.streams) &&
              streamsPayload.streams.length === 0 &&
              (hasAdminRights || isDemoMode)
            ) {
              destination = "/streams";
            }
          } catch {
            // Ignore stream fetch errors and fall back to the prior destination.
          }

          navigate(destination, { replace: true });
        } else {
          setSession(null);
          setError(data?.error ?? "Invalid username or password.");
        }
      } catch (err) {
        console.error(err);
        setError("Unable to sign in. Please try again.");
      } finally {
        setSubmitting(false);
      }
    }
  });

  if (!userManagementResolved) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userManagementEnabled) {
    return <Navigate to="/player" replace />;
  }

  return (
    <Box
      component="section"
      sx={{
        maxWidth: 440,
        mx: "auto",
        mt: { xs: 2, sm: 5 },
        p: { xs: 3, sm: 4 },
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 3,
        boxShadow: 3
      }}
    >
      <Typography component="h1" variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Sign in
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Enter your credentials to access the SkyView player.
      </Typography>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        noValidate
        sx={{
          mt: 3,
          display: "grid",
          gap: 2.5,
          // Browser autofill colors the input separately from its MUI outline and adornment.
          "& .MuiInputBase-input:autofill, & .MuiInputBase-input:-webkit-autofill, & .MuiInputBase-input:-webkit-autofill:hover, & .MuiInputBase-input:-webkit-autofill:focus": {
            WebkitBoxShadow: (theme) => `0 0 0 100px ${theme.palette.background.paper} inset`,
            WebkitTextFillColor: (theme) => theme.palette.text.primary,
            caretColor: (theme) => theme.palette.text.primary
          }
        }}
      >
        <TextField
          label="Username"
          name="username"
          value={formik.values.username}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          autoComplete="username"
          disabled={backendOffline || formik.isSubmitting}
          error={Boolean(formik.touched.username && formik.errors.username)}
          helperText={formik.touched.username ? formik.errors.username : ""}
        />
        <TextField
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          autoComplete="current-password"
          disabled={backendOffline || formik.isSubmitting}
          error={Boolean(formik.touched.password && formik.errors.password)}
          helperText={formik.touched.password ? formik.errors.password : ""}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  disabled={backendOffline || formik.isSubmitting}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                name="remember"
                checked={formik.values.remember}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  formik.setFieldValue("remember", event.target.checked);
                  persistRememberPreference(event.target.checked);
                }}
                color="primary"
                disabled={backendOffline || formik.isSubmitting}
              />
            }
            label="Remember me"
          />
          <FormHelperText sx={{ mt: -0.5, ml: 4.5 }}>
            Stay signed in across browser restarts until you log out.
          </FormHelperText>
        </Box>
        <Button
          type="submit"
          variant="contained"
          disabled={backendOffline || formik.isSubmitting || !formik.isValid}
          sx={{ py: 1.25, fontWeight: 600 }}
        >
          Sign in
        </Button>
      </Box>
      <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
        {backendOffline && (
          <Alert severity="warning">
            Backend server is offline. Sign in will be available once it reconnects.
          </Alert>
        )}
        {error && <Alert severity="info">{error}</Alert>}
      </Box>
    </Box>
  );
};

export default LoginPage;
